import { Check } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface SkipProps {
	/** Full product name, e.g. "Arbodienstverlening" — rendered as "Geen <productName>" */
	productName: string;
	/** True when no quote is currently selected for this product (selectedQuoteId === null) */
	isSelected: boolean;
	isDisabled: boolean;
	onSelect: () => void;
}

const Skip = ({ productName, isSelected, isDisabled, onSelect }: SkipProps): React.JSX.Element => {
	const { t } = useTranslation();

	function handleClick(): void {
		if (isDisabled || isSelected) return;
		onSelect();
	}

	function handleKeyDown(e: React.KeyboardEvent): void {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleClick();
		}
	}

	const label = t('productSelection.skipCard.noProduct', { productName });

	return (
		<div className="relative h-full w-full">
			{isSelected && (
				<div className="absolute -top-2 -right-2 z-10">
					<div className="w-5 h-5 rounded-full bg-muted-foreground/50 flex items-center justify-center shadow-md">
						<Check className="w-3 h-3 text-white" />
					</div>
				</div>
			)}
			<div
				className={cn(
					'border rounded-lg overflow-hidden bg-linear-to-br from-muted-foreground/2 to-muted-foreground/7 h-full flex flex-col',
					isSelected ? 'border-muted-foreground/12' : 'border-dashed border-border'
				)}
			>
				<div
					className={cn(
						'flex flex-col items-center justify-center px-5 gap-4 flex-1',
						isDisabled || isSelected ? 'pointer-events-none' : 'cursor-pointer'
					)}
					onClick={handleClick}
					onKeyDown={handleKeyDown}
					tabIndex={isDisabled || isSelected ? -1 : 0}
					role="button"
					aria-label={label}
					aria-pressed={isSelected}
					aria-disabled={isDisabled || isSelected}
				>
					{/* <X className="w-8 h-8 text-muted-foreground/30" /> */}
					<div
						className={cn(
							'font-semibold text-sm text-muted-foreground text-center leading-snug',
							!isSelected && 'opacity-50'
						)}
					>
						<span className="block">{t('productSelection.skipCard.geen')}</span>
						<span className="block">{productName}</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Skip;
